import React, {useCallback, useEffect, useState} from "react";
import { useSelector, useDispatch } from "react-redux";
import { NavLink, useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  TextField,
  Button,
  Grid,
  Typography,
  Divider,
} from "@mui/material";

import {
  PageContainer,
  Form,
  FormSection,
  CustomDatePicker,
  BasicTable,
  ConditionalContainer,
  ButtonModal,
} from "../../../../components";

import { ReferralCreateModal } from "../../..";
import { RequestStateChecker } from "../../../../functions";
import { getReferralByKey } from "../../../../redux/actions/referralActions";
import { CONTRACT_REQUEST_ROUTE } from "../../../../routes";
import EmployeeField from "../../../../components/Relations/EmployeeField";

export const KDIRequestInfoSection = ({ request, setRequest }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();

  const [requestId, setRequestId] = useState(id);

  const { clinicalRequest } = useSelector((s) => s.clinicalRequest);

  const stateCode = request?.state?.code?.toLowerCase();
  const state = new RequestStateChecker(stateCode);

  const [specifications, setSpecifications] = useState([]);
  const [objects, setObjects] = useState([]);
  const [referral, setReferral] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const handleChange = useCallback((key, value) => {
    setRequest({ ...request, [key]: value,  [`${key}Id`]: value?.id})
  }, [request]);

  useEffect(() => {
    setRequest(clinicalRequest);
    setObjects(clinicalRequest.objects);
    setSpecifications(clinicalRequest.specifications);
  }, [clinicalRequest]);

  useEffect(() => {
    if (!clinicalRequest?.referralId) return;
    dispatch(getReferralByKey(clinicalRequest.referralId)).then(setReferral);
  }, [dispatch, clinicalRequest?.referralId]);

  return (
    <>
      <Form onSubmit={handleSubmit}>
        <PageContainer>
          <FormSection heading="Заявка" mb={4}>
            <Grid container spacing={2}>
              {stateCode == 'declined' && <Grid item xs={12}>
                <label>Комментарий: {request?.comment}</label>
              </Grid>}
              <Grid item xs={3}>
                <TextField
                  label="Номер заявки"
                  type="number"
                  value={request?.number}
                  disabled
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  label="Сводная заявка"
                  type="text"
                  value={request?.contractRequestId}
                  disabled
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Автор заявки"
                  type="text"
                  value={request?.createdBy?.fullName}
                  disabled
                />
              </Grid>
              <Grid item xs={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <CustomDatePicker
                      label="Дата заявки"
                      value={request?.createdAt}
                      disabled
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </FormSection>
          <FormSection mb={4}>
            <Typography component="h4" align="left" variant="h6">
              Объекты ({objects?.length || 0})
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <BasicTable
                  cells={[
                    { id: "id", label: t("object.tableItem.id") },
                    { id: "name", label: t("object.tableItem.name") },
                    {
                      id: "locality",
                      label: t("object.tableItem.locality"),
                      render: (row) => row?.locality?.name,
                    },
                    {
                      id: "district",
                      label: t("object.tableItem.district"),
                      render: (row) => row?.district?.name,
                    },
                    {
                      id: "city",
                      label: t("object.tableItem.city"),
                      render: (row) => row?.city?.name,
                    },

                    {
                      id: "addressLine",
                      label: t("object.tableItem.addressLine"),
                    },
                  ]}
                  rows={objects}
                  disabled
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection mb={4}>
            <Typography component="h4" align="left" variant="h6">
              Услуги ({specifications?.length || 0})
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <BasicTable
                  cells={[
                    { id: "id", label: t("specification.tableItem.id") },
                    {
                      id: "object",
                      label: t("specification.tableItem.object"),
                      render: ({ object }) => object?.name,
                    },
                    {
                      id: "serviceKind",
                      label: t("specification.tableItem.serviceKind"),
                      render: ({ serviceKind }) => serviceKind?.name,
                    },
                    {
                      id: "service",
                      label: t("specification.tableItem.service"),
                      render: ({ service }) => service?.name,
                    },
                    {
                      id: "unit",
                      label: t("specification.tableItem.unit"),
                      render: ({ unit }) => unit?.name,
                    },
                    {
                      id: "schedule",
                      label: t("specification.tableItem.schedule"),
                      render: ({ schedule }) => schedule?.name,
                    },
                    {
                      id: "group",
                      label: 'Группа',
                      render: ({ service }) => service?.group?.name,
                    },
                    {
                      id: "method",
                      label: 'Метод',
                      render: ({ service }) => service?.kind?.name,
                    },
                    {
                      id: "count",
                      label: t("specification.tableItem.quantity"),
                    },
                    {
                      id: "contractCount",
                      label: t("specification.tableItem.quantityContract"),
                    },
                    { id: "price", label: t("specification.tableItem.price") },
                  ]}
                  rows={specifications}
                  disabled
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection heading="Назначение ответственных" mb={4}>
            <Grid container spacing={2}>
              <EmployeeField
                  style={{ marginBottom: "5px", paddingLeft: 0 }}
                  label={"Исполнитель заявки"}
                  object={request?.performer}
                  value={request?.performer?.id}
                  onChange={(val) => {
                    handleChange('performer', val)
                  }}
              />
              <EmployeeField
                  label={"Ответственный лаборант"}
                  object={request?.reponsiblePerformer}
                  value={request?.reponsiblePerformer?.id}
                  onChange={(val) => {
                    handleChange('reponsiblePerformer', val)
                  }}
              />
            </Grid>
          </FormSection>

          <FormSection heading="Сводная">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Button
                  variant="text"
                  component={NavLink}
                  to={CONTRACT_REQUEST_ROUTE(request?.contractRequestId)}
                >
                  Сводная заявка #{request?.contractRequestId}
                </Button>
              </Grid>
            </Grid>
          </FormSection>

          <ConditionalContainer
            condition={
              state.isProcessing() || state.isResearch() || state.isFinished()
            }
          >
            <Divider style={{ margin: "50px 0px" }} />
            <FormSection heading="Единоразовая заявка">
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <BasicTable
                    cells={[
                      { id: "id", label: t("request.tableItem.id") },
                      { id: "number", label: t("request.tableItem.number") },
                      {
                        id: "state",
                        label: t("request.tableItem.state"),
                        render: (row) => row?.state?.name,
                      },
                      {
                        id: "createdAt",
                        label: t("request.tableItem.createdAt"),
                      },
                    ]}
                    rows={referral ? [referral] : null}
                    button={
                      <Grid container columnSpacing={2}>
                        <Grid item>
                          <ButtonModal
                            modal={ReferralCreateModal}
                            externalState={{
                              KDIRequestId: requestId,
                            }}
                          >
                            Добавить
                          </ButtonModal>
                        </Grid>
                      </Grid>
                    }
                    disabled={!state.isProcessing()}
                  />
                </Grid>
              </Grid>
            </FormSection>
          </ConditionalContainer>
        </PageContainer>
      </Form>
    </>
  );
};
